import { type TranslationTree } from './ru.js';

export const en: TranslationTree = {
  substitutions: {
    nextPreview: 'Following send: #{{counter}}, date in message — {{date}}',
    title: 'Auto substitutions',
    hint: 'Issue number and date are resolved when sending. Insert a variable into the title or message.',
    start: 'Start at',
    step: 'Step',
    invalid:
      'Enable substitutions for {counter}. Start: integer from 0 to 1,000,000,000; step: 1 to 1,000,000. Date offset example: {date:+13d}.',
    sequence: 'Next issue: {{current}} → then {{next}}',
    offset: 'Date offset, days',
    destination: 'Insert into',
    toTitle: 'Title',
    toMessage: 'Message',
    insertCounter: 'Counter',
    insertDate: 'Date',
    policy:
      'Preview and pause do not consume numbers. Manual sending creates a new issue. Failed attempts keep their number for retries. The schedule stays unchanged.',
  },
  polls: {
    title: 'Poll',
    add: 'Add a poll',
    hint: 'A Telegram poll will appear below the message. Add options and let participants suggest their own.',
    question: 'Question',
    questionPlaceholder: 'What shall we choose?',
    options: 'Answer options',
    option: 'Option {{number}}',
    removeOption: 'Remove option {{number}}',
    addOption: 'Add option',
    allowAdding: 'Participants can add their own options',
    addingHint: 'Requires a public poll: Telegram will show who voted.',
    multiple: 'Allow multiple answers',
    anonymous: 'Anonymous poll',
    public: 'Public poll',
    ownOptionHint: 'Participants can add an option in Telegram',
    preview: 'Poll preview',
    duplicates: 'Each option must be different.',
    invalid: 'Enter a question and 2 to 12 unique answer options.',
  },
  labels: {
    role: 'Role',
    roles: {
      owner: 'Owner',
      admin: 'Administrator',
      moderator: 'Moderator',
      viewer: 'Viewer',
      unknown: 'Role not specified',
    },
    chatTypes: {
      private: 'Private chat',
      group: 'Group',
      supergroup: 'Supergroup',
      channel: 'Channel',
      unknown: 'Chat',
    },
  },
  workspace: {
    memberBirthday: 'Birthday · {{date}}',
    memberRoleTitle: 'Role · {{name}}',
    memberRoleError: 'Could not change the role. Please try again.',

    needsTitleAndMessage: 'Add a heading and message to choose recipients.',
    needsTitle: 'Add a heading to continue.',
    needsMessage: 'Add text or a completed rich block to continue.',
    needsRecipients: 'Choose at least one group to send to.',
    holdForActions: 'Hold a card to open its actions',

    quickShort: { hour: 'In an hour', tomorrow: 'Tomorrow, 9:00', monday: 'Mon, 9:00' },
    memberSearch: 'Search members',
    journalSearch: 'Search journal',
    filters: 'Filters',
    searchToggle: 'Search messages',
    statusLabel: 'Status',
    resetFilters: 'Reset',
    typeLabel: 'Message type',
    types: {
      task: 'Requires confirmation',
      birthday: 'Birthday',
      work: 'Work',
      custom: 'Other',
    },
    messageHeading: 'Message heading',
    quick: { hour: 'In an hour', tomorrow: 'Tomorrow at 09:00', monday: 'Monday at 09:00' },
    title: 'Your team, in sync',
    subtitle: 'Team messages and upcoming deliveries',
    upcoming: 'Coming up',
    upcomingHint: 'Times use each reminder’s time zone',
    noUpcoming: 'No upcoming deliveries',
    noUpcomingHint: 'Create a message or open your reminders to set a schedule.',
    allReminders: 'All reminders',
    create: 'Create message',
    attention: 'Needs attention',
    summary: 'Delivery overview',
    loadError: 'Could not load data. Check your connection and try again.',
    retry: 'Try again',
    resultCount: 'Found: {{count}}',
    sort: 'Reminder order',
    newest: 'Newest first',
    bySchedule: 'Next delivery',
    byTitle: 'Title',
    actions: 'Reminder actions',
    rich: 'Rich message',
    copy: 'Create similar',
    copyHint:
      'Content, formatting and recipients copied. Choose a date and review the preview before saving.',
    steps: 'Message creation steps',
    step: { message: 'Message', recipients: 'Recipients', preview: 'Review' },
    messageHint:
      'Prepare your message and choose when to send. Review the Telegram preview before sending.',
    previewHint: 'Review the formatting, recipients and delivery time.',
    recipientsHint: 'Choose groups and people responsible for this reminder.',
  },
  header: {
    analyticsHint: 'Delivery and team activity',
    templatesHint: 'Reusable text for new messages',
    spaceLabel: 'Your workspace',

    brand: 'IRL',
    title: 'Keep your team in sync',
    menu: 'Menu',
    settings: 'Settings',
  },
  nav: {
    home: 'Home',
    analytics: 'Analytics',
    reminders: 'Reminders',
    chats: 'Groups',
    logs: 'Journal',
    audit: 'Audit',
    templates: 'Templates',
    settings: 'Settings',
    createReminder: 'Create reminder',
  },
  auth: {
    accessDenied: 'Access denied',
    permissionDeniedHint: 'You do not have permission to view this section.',
    authError: 'Authentication error',
    telegramOnlyTitle: 'Telegram only',
    telegramOnlyDescription:
      'The admin panel is available only as a Mini App inside Telegram. Open the bot and tap the menu button.',
    telegramInitMissing:
      'Telegram did not provide auth data. Close the Mini App and open it again. If it keeps failing, check the domain in @BotFather.',
    networkError: 'Could not reach the server. Check your connection and try again.',
    devHint:
      'Local dev: set DEV_AUTH_BYPASS=true and DEV_AUTH_USER_ID=100000001 in .env, then pnpm db:seed and restart API',
    devBypassError:
      'Local dev: enable DEV_AUTH_BYPASS=true in .env, restart API and run pnpm db:seed',
  },
  deliveryMessage: {
    title: 'Edit sent message',
    edit: 'Edit sent message',
    hint: 'Only this message in Telegram will change. The template and future deliveries will stay the same.',
    approximate:
      'This text was restored from the template and may differ from the sent message. Review the entire text before saving.',
    loading: 'Loading message text…',
    loadError: 'Could not load the message text. Please try again.',
    saveError:
      'Could not edit the message. Check the text and the bot’s access to the message, then try saving again.',
    validationError: 'Enter a message between 1 and 4096 characters.',
    saved: 'Sent message updated',
    editedAt: 'Edited {{date}}',
  },
  common: {
    clear: 'Clear search',
    moveUp: 'Move up',
    moveDown: 'Move down',
    save: 'Save',
    saving: 'Saving…',
    back: 'Back',
    next: 'Next',
    all: 'All',
    open: 'Open',
    new: 'New',
    edit: 'Edit',
    delete: 'Delete',
    preview: 'Preview',
    search: 'Search…',
    noData: 'No data',
    selectChat: 'Select a group',
    allChats: 'All groups',
    dashboard: 'Dashboard',
    enabled: 'Enabled',
    active: 'Active',
    inactive: 'Inactive',
    group: 'Group',
    groupUnknown: 'Group not specified',
    pause: 'Pause',
    pausing: 'Pausing…',
    resume: 'Resume',
    start: 'Start',
    starting: 'Starting…',
    sending: 'Sending…',
    sendNow: 'Send now',
    sendNowTitle: 'Send now?',
    sendNowConfirm:
      'The message will be delivered to selected groups immediately. The scheduled run is unchanged.',
    sendFailed: 'Failed to send',
    deleting: 'Deleting…',
    activate: 'Activate',
    now: 'Now',
    actions: 'Actions',
    close: 'Close',
    optional: 'opt.',
    server: 'server',
    syncing: 'Syncing…',
    retry: 'Retry',
    loadError: 'Could not load data',
    saveError: 'Save failed',
    sendError: 'Send failed',
    deleteConfirm: 'Delete «{{title}}»?',
    deleteReminderTitle: 'Delete reminder?',
    deleteReminderConfirm: 'Delete this reminder?',
    deleteReminderConfirmDetail:
      '«{{title}}» will be permanently deleted along with its schedule and delivery history.',
    deleteTemplateConfirm: 'Delete template «{{name}}»?',
    selectItem: 'Select {{label}}',
    serverTime: 'Server time',
    previewPlaceholder: 'Notification text will appear here',
    drafts: 'Drafts',
    paused: 'Paused',
    archive: 'Archive',
    errors: 'Errors',
    cancel: 'Cancel',
    done: 'Done',
    change: 'Edit',
    settingsSaved: 'Settings saved',
    selectedCount: '{{count}} selected',
    year: 'Year',
    name: 'Name',
    style: 'Style',
    body: 'Text',
    message: 'Message',
    text: 'Text',
    timezone: 'Timezone',
    timezoneFull: 'Time zone',
    timezoneNow: 'Current time in this timezone: {{time}}',
    timezoneNowLabel: 'Current time in this timezone',
    notes: 'Notes',
    action: 'Action',
    frequency: 'Frequency',
    type: 'Type',
  },
  badge: {
    active: 'Active',
    paused: 'Paused',
    draft: 'Draft',
    inactive: 'Inactive',
    archived: 'Archived',
    sent: 'Delivered',
    failed: 'Failed',
    pending: 'Pending',
    cancelled: 'Cancelled',
    scheduled: 'Scheduled',
    due_no_job: 'No job',
    misconfigured: 'Misconfigured',
    awaiting_activation: 'Awaiting start',
    not_applicable: '—',
  },
  confirmations: {
    buttonSettings: 'Response buttons',
    actionNames: {
      done: 'Accept',
      decline: 'Decline',
    },
    buttonLabel: 'Button label',
    requireText: 'Require a text response',
    responsePrompt: 'Question for the participant',
    responsePromptPlaceholder: 'For example: suggest a name for the next sprint',
    buttonSettingsHint:
      'Keep at least one button enabled. When text is required, the answer is saved after the participant replies.',
    responseReceived: 'Response received',
    awaitingDelivery: 'The response period starts after delivery.',
    syncPending: 'Responses saved. Updating Telegram buttons…',
    syncUnavailable: 'The Telegram message cannot be updated. Responses are saved here.',

    invalidSettings:
      'Check the response period and buttons. Enable at least one button with a label; when follow-ups are enabled, their interval must be shorter than the response period.',
    title: 'Participant confirmation',
    audience: 'Who should respond',
    all: 'Everyone known to the bot',
    selected: 'Select participants',
    knownHint:
      'The bot knows people through messages, group events and synchronization. Telegram does not expose the full member list.',
    interval: 'Remind every, minutes',
    repeatEnabled: 'Repeat until answered',
    singleSend: 'One message with confirmation buttons, without follow-up reminders.',
    deadline: 'Response period, hours',
    fieldsHint:
      'One message is sent by default. If enabled, follow-ups stop after the participant responds, the response period ends or the notification is paused.',
    groupsHint: 'Choose groups on the next step, then select participants.',
    empty: 'No known participants yet. Ask them to write in the group and refresh the list.',
    pending: 'Waiting',
    done: 'Done',
    declined: 'Declined',
    progress: 'Participant responses',
    noRequests: 'Responses will appear after the notification is sent.',
    expired: 'Response period ended',
    paused: 'Follow-ups paused',
    needRecipients: 'Select at least one participant.',
    previewHint:
      'Telegram will show the configured buttons. If a button requires text, the bot will ask the participant to reply to its message.',
  },
  settings: {
    appearance: 'Appearance',
    darkTheme: 'Dark theme',
    themeHint: 'Soft light or deep evening tones',
    themeAuto: 'Follow Telegram',

    title: 'Settings',
    profile: 'Profile',
    about: 'About',
    aboutDescription: 'IRL — Info / Release / Lifecycle',
    version: 'Version',
    updated: 'Built',
    changelog: 'Release notes',
    language: 'Language',
    languageDescription: 'Mini App interface',
    profileHint: 'Telegram account',
    localeRu: 'Русский',
    localeEn: 'English',
    integrations: 'Integrations',
    telegramApiTitle: 'Advanced Telegram API access',
    telegramApiDescription: 'For member export and other MTProto scenarios',
    telegramApiManage: 'Set up',
    telegramApiViewStatus: 'Connection status',
    telegramApiConnected: 'Connected',
    telegramApiNotConnected: 'Not set up',
  },
  telegramApi: {
    optionalBadge: 'Optional',
    wizard: {
      intro: 'Overview',
      guide: 'Guide',
      form: 'Enter',
      stepOf: 'Step {{current}} of {{total}}',
      done: 'Done',
    },
    formSecurityNote: 'The secret is stored on the server only, not in the browser.',
    sourceLabel: 'Source: {{source}}',
    sourceEnv: 'environment (.env)',
    sourceDb: 'Mini App settings',
    title: 'Advanced Telegram API access',
    introLead:
      'The Mini App already signs you in via Telegram automatically. api_id and api_hash are only needed for advanced integration scenarios.',
    introSecondary: 'This step is optional. Most Mini App features work without it.',
    benefitsTitle: 'What you get',
    benefitExport: 'Full group member export via MTProto',
    benefitMtproto: 'Advanced scenarios not available through the standard Bot API',
    importantTitle: 'Good to know',
    importantOptional: 'Standard Mini App sign-in already works without these credentials.',
    importantMiniAppAuth: 'These credentials are only for advanced access.',
    importantSecret: 'api_hash is a secret. Do not share it with anyone.',
    openMyTelegram: 'Open my.telegram.org',
    setupLater: 'Set up later',
    guideTitle: 'How to get credentials',
    guideLead: 'Follow the steps on Telegram’s official site. It only takes a few minutes.',
    guideStep: {
      login: 'Sign in at my.telegram.org with your phone number',
      openTools: 'Open “API development tools”',
      openToolsHint: 'Usually in the menu after sign-in',
      copyCredentials: 'Create an app and copy api_id and api_hash',
    },
    externalSiteTitle: 'Official Telegram website',
    externalSiteHint:
      'You will open Telegram’s external site. Credentials are entered here and secrets are stored only on our server.',
    hashWarningTitle: 'Keep api_hash secret',
    hashWarningBody: 'Do not send api_hash in chats, screenshots, or to third-party services.',
    guideReady: 'I copied the credentials — enter them',
    formTitle: 'Enter API credentials',
    formLead:
      'Values from my.telegram.org. api_hash is stored on the server only and is not kept in the browser.',
    apiIdLabel: 'API ID',
    apiIdHint: 'Numeric app identifier from my.telegram.org',
    apiHashLabel: 'API Hash',
    apiHashHint: 'Secret app key. Stored on the server only.',
    showHash: 'Show API Hash',
    hideHash: 'Hide API Hash',
    saveButton: 'Verify and save',
    saving: 'Saving…',
    saveSuccess: 'Telegram API credentials saved',
    saveError: 'Could not save credentials. Check the values you entered.',
    statusTitle: 'Connection status',
    statusLead: 'Advanced Telegram API connection for MTProto scenarios.',
    statusConnected: 'Connected',
    statusDisconnected: 'Not connected',
    envManagedTitle: 'Managed by environment',
    envManagedHint:
      'TELEGRAM_API_ID and TELEGRAM_API_HASH are set in the server .env. They cannot be changed from the Mini App.',
    verificationLabel: 'Verification',
    verificationVerified: 'Connection verified',
    verificationSaved: 'Saved',
    verificationUnverified: 'Not verified',
    connectedByLabel: 'Connected by',
    connectedAtLabel: 'Connected at',
    updatedAtLabel: 'Last updated',
    updateButton: 'Update',
    verifyButton: 'Test connection',
    verifying: 'Testing…',
    verifySuccess: 'Connection verified successfully',
    verifyError: 'Could not verify connection',
    dangerZoneTitle: 'Disconnect integration',
    dangerZoneHint:
      'Removes api_id and api_hash stored on the server. MTProto features will stop working until configured again.',
    disconnectButton: 'Disconnect',
    disconnecting: 'Disconnecting…',
    disconnectSuccess: 'Integration disconnected',
    disconnectError: 'Could not disconnect integration',
    loadError: 'Could not load connection status',
    exportSetupCta: 'Set up Telegram API',
  },
  feedback: {
    saved: 'Saved',
    savedAt: 'Saved at {{time}}',
    settingsUpdated: 'Settings updated',
    templateApplied: 'Template applied',
    updated: 'Changes saved',
    reminderCreated: 'Reminder created',
    reminderSent: 'Reminder sent',
    sent: 'Sent',
    actionFailed: 'Action failed',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Operational overview',
    analyticsLink: 'Analytics',
    periods: { today: 'Today', '7d': '7 days', '30d': '30 days' },
    metrics: {
      sent: 'Sent',
      delivered: 'Delivered',
      errors: 'Errors',
      groups: 'Groups',
      activeGroups: 'active',
    },
    alerts: 'Needs attention',
    recentActivity: 'Delivery activity',
    allLogs: 'All logs',
    viewLogs: 'View logs',
    noDeliveries: 'No deliveries yet',
  },
  analytics: {
    title: 'Analytics',
    subtitle: 'Read models by group',
    dashboardLink: 'Dashboard',
    periods: { today: 'Today', '7d': '7 days', '30d': '30 days' },
    metrics: {
      reminders: 'Reminders',
      groups: 'Groups',
      inScope: 'in scope',
      delivery: 'delivery',
      sent: 'sent',
    },
    timeline: 'Timeline',
    timelineHint: 'Reminders and birthdays by day',
    noTimeline: 'No data for period',
    byChat: 'By group',
    noActivity: 'No activity',
    remindersShort: 'rem.',
    errorsShort: 'errors',
  },
  pages: {
    reminders: 'Reminders',
    createReminder: 'Create reminder',
    selectGroups: 'Select groups',
    previewSend: 'Preview & send',
    editReminder: 'Edit reminder',
    chats: 'Groups',
    logs: 'Journal',
    templates: 'Templates',
    editTemplate: 'Edit template',
    createTemplate: 'Create template',
    greetings: 'Greetings',
    members: 'Members',
    membersEmpty:
      'Members appear after Telegram sync. Group admins load immediately; others appear when they post in the chat or join the group.',
    globalAdminBadge: 'Global administrator (WEBAPP_ADMINS)',
    audit: 'Audit',
    birthdaySettings: 'Birthday settings',
    birthdayMembers: 'Members',
    birthdayTemplates: 'Greeting templates',
    birthdayTemplateEdit: 'Edit template',
    birthdayTemplateNew: 'New template',
    birthdayPreview: 'Greeting preview',
    birthdayLogs: 'Greeting log',
  },
  chats: {
    search: 'Search groups...',
    subtitle: 'Manage groups and quick actions',
    primaryAction: 'Reminders',
    moreActions: 'More actions',
    filters: { all: 'All', active: 'Active', inactive: 'Inactive' },
    emptyTitle: 'No connected groups',
    emptyDescription: 'Add the bot to a group, make it admin, then open the bot in DM',
    noTitle: 'Untitled',
    remindersCount: '{{count}} reminders',
    detailSubtitle: 'Group sections and settings',
    actionsTitle: 'Sections',
    notFound: 'Group not found',
    notFoundHint: 'You may no longer have access to this group.',
    avatarAlt: 'Avatar of {{title}}',
    dangerZoneTitle: 'Danger zone',
    actions: {
      reminders: 'Reminders',
      activity: 'Activity log',
      members: 'Members',
      greetings: 'Greetings',
    },
  },
  reminders: {
    loadingMore: 'Loading more reminders…',
    allLoaded: 'All reminders loaded',

    schedulingErrorReasons: {
      missing_schedule:
        'The next run was not scheduled. Check the schedule and start the reminder.',
      once_in_past:
        'The one-time date has passed without a confirmed delivery. Choose a new date and start the reminder.',
      period_ended: 'The schedule has ended. Change the end date and start the reminder.',
      no_future_run: 'No future run was found. Check the dates and recurrence settings.',
      invalid_schedule:
        'The schedule could not be calculated. Check its date, time and recurrence settings.',
    },
    noRepeatingSchedule: 'No automatic delivery is scheduled. Send now or choose a new date.',
    onceCompleted: 'No further automatic delivery is scheduled. Send now or choose a new date.',
    search: 'Search reminders...',
    subtitle: 'List, filters, and quick actions',
    createShort: 'Create',
    empty: 'No reminders yet',
    emptySearch: 'No results',
    emptyHint: 'Create your first notification for group delivery',
    noGroupsSearch: 'No groups found',
    createFirst: 'Create reminder',
    groupsCount: '{{count}} groups',
    deleteAll: 'Delete all reminders',
    deleteAllConfirm: 'Delete all',
    deleteAllTitle: 'Delete all group reminders?',
    deleteAllDescription:
      'All reminders in «{{title}}» will be permanently deleted — active, drafts, paused, and archived. Their schedules will be cancelled too.',
    deleteAllHint:
      'Permanently deletes every reminder in this group, including drafts and archive.',
    deleteAllSuccess: 'Deleted reminders: {{count}}',
    deleteAllEmpty: 'This group has no reminders',
    filters: {
      all: 'All',
      active: 'Active',
      draft: 'Drafts',
      paused: 'Paused',
      inactive: 'Inactive',
      archived: 'Archive',
    },
    form: {
      createTitle: 'Create reminder',
      editTitle: 'Edit reminder',
      titleLabel: 'Reminder title',
      titlePlaceholder: 'e.g. Service update',
      bodyPlaceholder: 'Notification text...',
      weekdayError: 'Select at least one weekday',
      contentType: { text: 'Text', photo: 'Photo', document: 'Document' },
      sendMode: { now: 'Send now', schedule: 'Schedule' },
      sending: 'Sending...',
      richFormatToggle: 'Block builder',
      richFormatHint: 'Headings, lists, tables, and other formatting',
      dragBlock: 'Drag block',
    },
    richMedia: {
      photo: 'Photo',
      collage: 'Collage',
      slideshow: 'Slideshow',
      addPhoto: 'Add photo',
      addPhotos: 'Add photos',
      replace: 'Replace',
      empty: 'Choose an image',
      emptyGroup: 'Add one or more photos',
      captionPlaceholder: 'Caption (optional)',
      uploadFailed: 'Could not upload the photo',
      previewError: 'No preview',
      photoAlt: 'Reminder photo',
      removePhoto: 'Remove photo',
      toolbar: {
        heading: 'Heading',
        paragraph: 'Paragraph',
        list: 'List',
        quote: 'Quote',
        pullQuote: 'Pull quote',
        table: 'Table',
        details: 'Details',
        divider: 'Divider',
        dateTime: 'Date/time',
        code: 'Code',
        photo: 'Photo',
        collage: 'Collage',
        slideshow: 'Slideshow',
      },
    },
    detail: {
      history: 'Delivery history',
      noHistory: 'No deliveries yet',
      schedule: 'Schedule',
      targets: 'Groups',
      responsible: 'Responsible admins',
      responsibleHint: 'Admins assigned to this reminder',
      scheduleAt: '{{type}} at {{time}} ({{timezone}})',
      deliveryHistoryHint: 'Recent deliveries for this reminder',
      scheduledFor: 'Scheduled: {{date}}',
      deliveredAt: 'Delivered: {{date}}',
      nextRunLabel: 'Next run',
      lastRunLabel: 'Last sent',
      tabOverview: 'Overview',
      tabDebug: 'Delivery / Debug',
    },
    notFound: 'Reminder not found',
    formSubtitle: 'Configure content and delivery schedule',
    saveDraft: 'Save draft',
    sendSuccess: 'Message sent to the group',
    nextRun: 'Next run: {{date}} · {{timezone}}',
    lastRun: 'Last sent: {{date}}',
    schedulingHealth: 'Queue status',
    reschedule: 'Rebuild schedule',
    rescheduling: 'Rebuilding…',
    rescheduleHint: 'No queue job found. Rebuild the schedule to restore delivery.',
    dueNoJobTitle: 'No queue job',
    misconfiguredTitle: 'Schedule misconfigured',
    deliveryStatus: 'Delivery',
    lastDeliveryError: 'Last error: {{error}}',
    queueState: 'Queue: {{state}}',
    queueJobState: {
      delayed: 'delayed',
      waiting: 'waiting',
      active: 'running',
      completed: 'completed',
      failed: 'failed',
      paused: 'paused',
      missing: 'not found',
    },
    scheduleMismatch: 'Queue mismatch: {{reason}}',
    startDateLocal: 'Calendar date: {{date}}',
    scheduleRevision: 'Schedule revision {{revision}}',
    noNextRun: 'No next send scheduled',
    schedulingHealthHelp: {
      scheduled: 'A BullMQ job is queued. Delivery will run automatically at the scheduled time.',
      due_no_job:
        'The reminder is active and the next run is set, but BullMQ has no delayed job. Tap “Rebuild schedule” — it will not send immediately, only restore the queue.',
      misconfigured:
        'The reminder is inactive because of a schedule error. Correct it and start it again.',
      awaiting_activation: 'Draft with a schedule. Press “Start” to enqueue delivery.',
    },
    debug: {
      queue: {
        title: 'Queue status',
        intro:
          'The scheduler stores a delayed job in Redis (BullMQ). When the time comes, a worker sends the message to groups automatically.',
        loadError: 'Failed to load queue status',
        noData: 'No queue data',
        retry: 'Retry',
        inSync: 'In sync',
        outOfSync: 'Out of sync',
        countdownLabel: 'Until delivery',
        countdownRemaining: 'Remaining',
        countdownDue: 'Due now',
        countdownDueHint:
          'The queued job should start within seconds. If nothing is sent, check the worker or rebuild the queue job.',
        countdownDays: '{{count}} d',
        countdownHours: '{{count}} h',
        countdownMinutes: '{{count}} min',
        countdownSeconds: '{{count}} s',
        jobStateHelp: {
          delayed:
            'The job is delayed in Redis and will fire at the scheduled time. «Delayed» is normal for an active reminder.',
          waiting: 'The job is waiting in the queue and will be picked up soon.',
          active: 'The job is running right now — delivery to groups is in progress.',
          completed:
            'The job finished. If delivery just happened, the next job appears after the schedule is recalculated.',
          failed: 'The job failed. Check delivery history and rebuild the queue job if needed.',
          paused: 'The job is paused in the queue — delivery is on hold until resumed.',
          missing:
            'No Redis job exists for this reminder. Nothing will be sent until the job is rebuilt.',
        },
        mismatch: {
          job_missing:
            'The database has a next run time, but the Redis job is missing. Rebuild the queue job.',
          scheduled_for_mismatch:
            'The Redis job time does not match database nextRunAt. Delivery might use a stale slot.',
          revision_mismatch:
            'The schedule changed after the job was enqueued — the Redis revision is stale.',
          missing_next_run_at:
            'Next run is not set in the database although the reminder may be active.',
        },
        mismatchTitle: 'Queue and database out of sync',
        dbSourceTitle: 'Database',
        dbSourceHint: 'nextRunAt field in MongoDB',
        redisSourceTitle: 'Redis queue',
        redisSourceHint: 'BullMQ delayed job',
        jobNotFound: 'Job not found',
        revision: 'DB revision #{{db}}{{job}}',
        revisionJob: ' · job #{{revision}}',
        technicalDetails: 'Technical details',
        actionsTitle: 'Admin actions',
        rescheduleButton: 'Rebuild queue job',
        rescheduleHint:
          'Removes the old Redis job and creates a new one from the current schedule. Does not send immediately.',
        rescheduleTitle: 'Rebuild queue job?',
        rescheduleConfirm:
          'Next run will be recalculated, schedule revision incremented, and a new delayed BullMQ job created. The message will not be sent now.',
        recalculateButton: 'Recalculate schedule',
        recalculateHint:
          'Recomputes nextRunAt from schedule rules and syncs the queue if the time changed.',
        recalculateTitle: 'Recalculate next run?',
        recalculateConfirm:
          'The next delivery time will be computed again from current schedule settings. If it changes, the queue updates automatically.',
        rescheduling: 'Rebuilding…',
        recalculating: 'Recalculating…',
      },
      delivery: {
        title: 'Next delivery',
        loadError: 'Failed to load delivery data',
        notFound: 'Reminder not found',
        retry: 'Retry',
        inSync: 'In sync',
        outOfSync: 'Out of sync',
        notScheduledTitle: 'Delivery not scheduled',
        notScheduledReason: 'Reason: {{reason}}',
        notScheduledDefault: 'Next run is not set — the scheduler does not know when to send.',
        recalculate: 'Recalculate',
        recalculating: 'Recalculating…',
        dbSourceTitle: 'Database',
        dbSourceHint: 'nextRunAt field in MongoDB',
        redisSourceTitle: 'Redis queue',
        redisSourceHint: 'BullMQ job in Redis',
        jobNotFound: 'Job not found',
        mismatchHint: 'Database and queue times do not match — delivery might use a stale slot.',
        technicalDetails: 'Technical details',
        copyScheduledForMs: 'Copy scheduledForMs',
      },
    },
    wizard: {
      step1: 'Step 1 of 3 — content',
      step2: 'Step 2 of 3 — groups',
      step3: 'Step 3 of 3 — send',
      stepShort1: 'Content',
      stepShort2: 'Groups',
      stepShort3: 'Send',
      progressShort: 'Step {{current}} of {{total}}',
      progress: 'Step {{current}} of {{total}} — {{step}}',
    },
    scheduleCard: {
      title: 'Schedule',
      description: 'When and how often to send the reminder',
    },
    previewRuns: {
      title: 'Upcoming runs',
      description: 'Preview based on current settings',
      today: 'Today',
      tomorrow: 'Tomorrow',
      soon: 'Soon',
      lessThanMinute: 'Less than a minute',
      inMinutes: 'In {{count}} min',
      inHours: 'In {{count}} h',
      inDays: 'In {{count}} d',
    },
    preview: {
      type: 'Type',
      groups: 'Groups',
      responsible: 'Responsible',
      schedule: 'Schedule',
      deliveryEstimate: 'Delivery estimate',
      errors7d: 'Errors (7d)',
      minutes: '~{{count}} min',
    },
    responsible: {
      selectChatsFirst: 'Select groups to assign responsible admins',
      hint: 'Group admins responsible for this reminder (optional)',
      noAdmins: 'No admins found in selected groups',
      label: 'Responsible admins',
    },
  },
  logs: {
    subtitle: 'Deliveries and admin changes in one timeline',
    chatFilter: 'Group',
    period: 'Period',
    status: 'Status',
    empty: 'No activity yet',
    emptyHint: 'Deliveries and changes will appear here',
  },
  activity: {
    changeBadge: 'Change',
    feed: {
      all: 'All',
      deliveries: 'Deliveries',
      changes: 'Admin changes',
    },
    roleChange: '{{from}} → {{to}}',
    health: {
      okTitle: 'All good',
      okMessage: '{{sent}} successful deliveries in this period',
      emptyTitle: 'No deliveries yet',
      emptyMessage: 'Nothing sent in this period — create a reminder to get started',
      pendingTitle: 'Deliveries in progress',
      pendingMessage: '{{pending}} pending, {{sent}} already sent',
      errorsTitle: 'Delivery issues found',
      errorsMessage: '{{failed}} failed, {{sent}} successful — check errors below',
      viewErrors: 'Show failed only',
    },
    actions: {
      reminder_created: 'Reminder created',
      reminder_updated: 'Reminder updated',
      reminder_deleted: 'Reminder deleted',
      reminder_paused: 'Reminder paused',
      reminder_resumed: 'Reminder resumed',
      reminder_archived: 'Reminder archived',
      chat_connected: 'Group connected',
      chat_disconnected: 'Group disconnected',
      member_role_changed: 'Member role changed',
      integration_telegram_api_saved: 'Telegram API credentials saved',
      integration_telegram_api_removed: 'Telegram API credentials removed',
      integration_telegram_api_verified: 'Telegram API verified',
    },
  },
  audit: {
    empty: 'No audit entries yet',
  },
  templates: {
    search: 'Search templates...',
    namePlaceholder: 'e.g. Daily standup',
    bodyPlaceholder: 'Template text...',
    createTitle: 'Create template',
    editTitle: 'Edit template',
    empty: 'No templates yet',
    createFirst: 'Create your first template',
    subtitle: 'Reusable text for reminders',
    messageTemplates: 'Message templates',
    noTemplatesForChat: 'No templates for this group.',
    emptyPicker: 'No templates yet — create one to reuse text in reminders.',
    applyHint: 'Tap a template to insert its text into the message.',
    createLink: 'Create',
    allLink: 'All →',
  },
  greetings: {
    placeholderHint: 'Variables: {name}, {username}, {chat}',
    welcomeEnable: 'Enable welcome message',
    goodbyeEnable: 'Enable goodbye message',
    welcomeTitle: 'Welcome message',
    welcomeDescription: 'Sent when a member joins the group',
    goodbyeTitle: 'Goodbye message',
    goodbyeDescription: 'Sent when a member leaves the group',
  },
  timezones: {
    Europe_Kaliningrad: 'Kaliningrad',
    Europe_Moscow: 'Moscow',
    Europe_Samara: 'Samara',
    Asia_Yekaterinburg: 'Yekaterinburg',
    Asia_Omsk: 'Omsk',
    Asia_Krasnoyarsk: 'Krasnoyarsk',
    Asia_Irkutsk: 'Irkutsk',
    Asia_Yakutsk: 'Yakutsk',
    Asia_Vladivostok: 'Vladivostok',
    Asia_Magadan: 'Magadan',
    Asia_Kamchatka: 'Kamchatka',
    Europe_Minsk: 'Minsk',
    Asia_Almaty: 'Almaty',
    Asia_Tbilisi: 'Tbilisi',
    Asia_Baku: 'Baku',
    UTC: 'UTC',
  },
  quickCreate: {
    title: 'Quick create',
    subtitle: 'Pick a mode — you can fine-tune details on the next step',
    now: 'Send now',
    weekdays: 'Weekdays',
  },
  recurrence: {
    typeLabel: 'Repeat',
    whenLabel: 'When to send',
    intervalLabel: 'Repeat every',
    endsLabel: 'Ends',
    summaryLabel: 'Summary',
    intervalUnit: {
      minute: 'minutes',
      hour: 'hours',
      day: 'days',
      week: 'weeks',
      month: 'months',
      year: 'years',
    },
    types: {
      once: 'Once',
      daily: 'Every day',
      weekly: 'On weekdays',
      monthly: 'Every month',
      yearly: 'Every year',
      interval: 'Every N…',
    },
    ends: {
      never: 'Never',
      on_date: 'On date',
      after_count: 'After N times',
    },
    presets: {
      '15m': '15 min',
      '30m': '30 min',
      '1h': '1 hour',
      '2h': '2 hours',
      '6h': '6 hours',
      '7d': '7 days',
      '14d': '14 days',
      '2w': '2 weeks',
      '1m': 'Month',
      '2m': '2 months',
      '3m': 'Quarter',
      '6m': '6 months',
    },
    occurrences: 'Number of repeats',
    summary: {
      date: '{{date}}',
      once: 'Once on {{date}} at {{time}}',
      daily: 'Every day at {{time}}',
      weekly: 'Every {{days}} at {{time}}',
      monthly: 'Every month on the {{days}} at {{time}}',
      yearly: 'Every year on {{day}} {{month}} at {{time}}',
      interval: 'Every {{interval}} {{unit}}, from {{start}}, at {{time}}',
    },
  },
  schedule: {
    weekdays: 'Weekdays',
    dayOfMonth: 'Day of month',
    daysOfMonth: 'Days of month',
    anchorMonth: 'Cycle start month',
    month: 'Month',
    day: 'Day',
    startDate: 'Send date',
    endDate: 'End date',
    hasEndDate: 'Limit repeat period',
    intervalStart: 'Start from date',
    intervalDays: 'Repeat every N days',
    time: 'Time',
    hours: 'Hours',
    minutes: 'Minutes',
    presets: {
      weekdays: 'Weekdays',
      weekend: 'Weekend',
      everyDay: 'Every day',
    },
    cycleHint: {
      bimonthly: 'Every 2 months, starting from {{month}}',
      quarterly: 'Every quarter (every 3 months), starting from {{month}}',
      semiannual: 'Every 6 months, starting from {{month}}',
    },
    types: {
      once: 'Once',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      bimonthly: 'Every 2 months',
      quarterly: 'Quarterly',
      semiannual: 'Twice a year',
      yearly: 'Yearly',
      interval: 'Every N days',
    },
  },
  weekdays: {
    short: {
      '0': 'Sun',
      '1': 'Mon',
      '2': 'Tue',
      '3': 'Wed',
      '4': 'Thu',
      '5': 'Fri',
      '6': 'Sat',
    },
    long: {
      '0': 'Sunday',
      '1': 'Monday',
      '2': 'Tuesday',
      '3': 'Wednesday',
      '4': 'Thursday',
      '5': 'Friday',
      '6': 'Saturday',
    },
  },
  months: {
    '1': 'January',
    '2': 'February',
    '3': 'March',
    '4': 'April',
    '5': 'May',
    '6': 'June',
    '7': 'July',
    '8': 'August',
    '9': 'September',
    '10': 'October',
    '11': 'November',
    '12': 'December',
  },
};
